import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, ScrollText, Users, GraduationCap,
  CreditCard, Megaphone, Inbox, LogOut, Menu, X, LineChart, UserCircle, ClipboardCheck, FolderOpen,
  Newspaper, Images, UserCog,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { images } from "../../data/content";
import api from "../../lib/api";

function useReviewCount(enabled) {
  const [count, setCount] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return;
    const load = () => api.get("/cms/review-count").then(({ data }) => setCount(data.count)).catch(() => {});
    load();
    window.addEventListener("cms:changed", load);
    return () => window.removeEventListener("cms:changed", load);
  }, [enabled, pathname]);
  return count;
}

function NavItem({ item, onClick }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-4 py-2.5 font-sans text-sm transition-colors ${
          isActive ? "bg-gold text-sapphire-deep font-semibold" : "text-parchment/75 hover:bg-white/5 hover:text-gold"
        }`
      }
      data-testid={`portal-nav-${item.tid}`}
    >
      <item.icon size={18} /> <span className="flex-1">{item.label}</span>
      {item.badge > 0 && (
        <span className="min-w-[22px] rounded-full bg-garnet px-1.5 py-0.5 text-center text-[11px] font-bold text-parchment" data-testid="review-count-badge">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

const nav = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "dashboard" },
  { to: "/portal/profile", label: "My Engagement", icon: UserCircle, tid: "profile" },
  { to: "/portal/calendar", label: "Calendar & Summonses", icon: CalendarDays, tid: "calendar" },
  { to: "/portal/minutes", label: "Minutes Archive", icon: ScrollText, tid: "minutes" },
  { to: "/portal/documents", label: "Documents", icon: FolderOpen, tid: "documents" },
  { to: "/portal/directory", label: "Member Directory", icon: Users, tid: "directory" },
  { to: "/portal/ritual", label: "Ritual & Education", icon: GraduationCap, tid: "ritual" },
  { to: "/portal/dues", label: "Dues & Payments", icon: CreditCard, tid: "dues" },
  { to: "/portal/announcements", label: "Announcements", icon: Megaphone, tid: "announcements" },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const links = [...nav];
  if (user?.is_officer) {
    links.splice(2, 0, { to: "/portal/engagement", label: "Engagement KPIs", icon: LineChart, tid: "engagement" });
    links.splice(3, 0, { to: "/portal/attendance", label: "Record Attendance", icon: ClipboardCheck, tid: "attendance" });
  }
  if (user?.is_admin) {
    links.push({ to: "/portal/inquiries", label: "Inquiries", icon: Inbox, tid: "inquiries" });
  }
  const cms = user?.cms || {};
  const reviewCount = useReviewCount(!!cms.reviewer);
  const contentLinks = [];
  if (cms.author || cms.reviewer) {
    contentLinks.push({ to: "/portal/content", label: "Content", icon: Newspaper, tid: "content", badge: reviewCount });
    contentLinks.push({ to: "/portal/media", label: "Media Library", icon: Images, tid: "media" });
  }
  if (cms.admin) contentLinks.push({ to: "/portal/cms-users", label: "Users & Roles", icon: UserCog, tid: "cms-users" });

  return (
    <div className="flex min-h-screen bg-ivory text-ink">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 transform border-r border-gold/20 bg-sapphire-deep transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="portal-sidebar"
      >
        <div className="flex h-20 items-center gap-3 border-b border-white/10 px-6">
          <img src={images.crestSeal} alt="Lodge crest" className="h-11 w-11" />
          <div className="font-display text-lg leading-tight text-parchment">
            Inner Lodge
            <span className="block font-sans text-[9px] uppercase tracking-[0.3em] text-gold">Portal · No. 508</span>
          </div>
        </div>

        <nav className="flex max-h-[calc(100vh-13rem)] flex-col gap-1 overflow-y-auto p-4">
          {links.map((item) => <NavItem key={item.to} item={item} onClick={() => setOpen(false)} />)}
          {contentLinks.length > 0 && (
            <>
              <p className="mb-1 mt-5 px-4 font-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-gold/70" data-testid="portal-nav-content-heading">
                Website Content
              </p>
              {contentLinks.map((item) => <NavItem key={item.to} item={item} onClick={() => setOpen(false)} />)}
            </>
          )}
        </nav>

        <div className="absolute inset-x-0 bottom-0 border-t border-white/10 p-4">
          <div className="mb-3 px-2">
            <p className="font-display text-base text-gold">{user?.name}</p>
            <p className="font-sans text-xs text-parchment/50">{user?.rank}</p>
          </div>
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 font-sans text-sm text-parchment/75 transition hover:bg-garnet/30 hover:text-parchment"
            data-testid="portal-logout"
          >
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-20 items-center justify-between border-b border-sapphire-deep/10 bg-ivory/90 px-6 backdrop-blur lg:px-10">
          <div className="flex items-center gap-4">
            <button className="text-sapphire-deep lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="portal-menu-toggle">
              <Menu size={24} />
            </button>
            <Link to="/" className="font-sans text-xs uppercase tracking-widest text-sapphire-deep/60 hover:text-gold-deep">
              ← Public Site
            </Link>
          </div>
          <span className="font-display text-lg text-sapphire-deep">{greeting()}, {user?.name?.split(" ").slice(-1)}</span>
        </header>

        <main className="flex-1 p-6 lg:p-10">
          <Outlet />
        </main>
      </div>

      {open && (
        <button className="fixed right-4 top-6 z-50 text-parchment lg:hidden" onClick={() => setOpen(false)} aria-label="Close">
          <X size={26} />
        </button>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
